import { z } from "zod";
import {
  LocalDateSchema,
  PlanningOpaqueIdSchema,
  PlanningRequestIdSchema,
  VerificationPolicySchema,
} from "./planning";

const IsoWeekdaySchema = z.number().int().min(1).max(7);
const LocalTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/u);

export const RoutineScheduleSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("daily"), interval_days: z.number().int().min(1).max(365) }).strict(),
  z
    .object({
      kind: z.literal("weekly"),
      interval_weeks: z.number().int().min(1).max(52),
      weekdays: z.array(IsoWeekdaySchema).min(1).max(7),
    })
    .strict()
    .refine((value) => new Set(value.weekdays).size === value.weekdays.length, {
      message: "weekdays must be unique",
      path: ["weekdays"],
    }),
  z
    .object({
      kind: z.literal("monthly_days"),
      days: z
        .array(z.union([z.number().int().min(1).max(31), z.literal("last")]))
        .min(1)
        .max(32),
    })
    .strict()
    .refine((value) => new Set(value.days).size === value.days.length, {
      message: "days must be unique",
      path: ["days"],
    }),
  z
    .object({
      kind: z.literal("monthly_nth_weekday"),
      ordinal: z.union([
        z.literal(-1),
        z.literal(1),
        z.literal(2),
        z.literal(3),
        z.literal(4),
        z.literal(5),
      ]),
      weekday: IsoWeekdaySchema,
    })
    .strict(),
]);

export const RoutinePauseRangeSchema = z
  .object({ start_local_date: LocalDateSchema, end_local_date: LocalDateSchema })
  .strict()
  .refine((value) => value.end_local_date >= value.start_local_date, {
    message: "pause end must not be earlier than pause start",
    path: ["end_local_date"],
  });

export const RoutineAvailabilitySchema = z
  .object({
    period: z.enum(["anytime", "morning", "after_school", "evening", "custom"]),
    from_local_time: LocalTimeSchema.nullable().default(null),
    until_local_time: LocalTimeSchema.nullable().default(null),
  })
  .strict()
  .refine(
    (value) =>
      value.period === "custom" ||
      (value.from_local_time === null && value.until_local_time === null),
    { message: "only custom availability accepts explicit times", path: ["period"] },
  )
  .refine(
    (value) =>
      value.from_local_time === null ||
      value.until_local_time === null ||
      value.until_local_time > value.from_local_time,
    { message: "availability end must be later than start", path: ["until_local_time"] },
  );

export const RoutineEvidencePolicySchema = z
  .object({ required: z.enum(["none", "photo", "note", "photo_or_note"]).default("none") })
  .strict();

export const RoutineNotificationPolicySchema = z
  .object({
    child_reminder: z.boolean().default(false),
    guardian_digest: z.boolean().default(false),
  })
  .strict();

export const RoutineParticipantSchema = z
  .object({
    child_id: PlanningOpaqueIdSchema,
    display_name: z.string().min(1).max(80),
    theme_color: z.string().regex(/^#[0-9a-f]{6}$/u),
  })
  .strict();

export const RoutineVersionSchema = z
  .object({
    id: PlanningOpaqueIdSchema,
    version_number: z.number().int().min(1),
    schedule: RoutineScheduleSchema,
    timezone: z.string().min(1).max(64),
    start_local_date: LocalDateSchema,
    end_local_date: LocalDateSchema.nullable(),
    availability: RoutineAvailabilitySchema,
    due_offset_days: z.number().int().min(0).max(30),
    default_duration_minutes: z.number().int().min(0).max(1440).nullable(),
    verification_policy: VerificationPolicySchema,
    evidence_policy: RoutineEvidencePolicySchema,
    notification_policy: RoutineNotificationPolicySchema,
    exception_dates: z.array(LocalDateSchema).max(366),
    pause_ranges: z.array(RoutinePauseRangeSchema).max(52),
    calendar_ids: z.array(PlanningOpaqueIdSchema).max(20),
    effective_at: z.number().int(),
    created_at: z.number().int(),
  })
  .strict();

export const RoutineMaterializationSchema = z
  .object({
    id: PlanningOpaqueIdSchema,
    child_id: PlanningOpaqueIdSchema,
    occurrence_key: z.string().min(10).max(160),
    planned_local_date: LocalDateSchema,
    task_id: PlanningOpaqueIdSchema,
    status: z.literal("materialized"),
  })
  .strict();

export const RoutineSchema = z
  .object({
    id: PlanningOpaqueIdSchema,
    workspace_id: PlanningOpaqueIdSchema,
    name: z.string().min(1).max(160),
    child_copy: z.string().min(1).max(160),
    notes: z.string().max(8000),
    area_id: PlanningOpaqueIdSchema.nullable(),
    project_id: PlanningOpaqueIdSchema.nullable(),
    term_id: PlanningOpaqueIdSchema.nullable(),
    status: z.enum(["active", "paused"]),
    color: z.string().regex(/^#[0-9a-f]{6}$/u),
    current_version: RoutineVersionSchema,
    participants: z.array(RoutineParticipantSchema).min(1).max(20),
    upcoming: z.array(RoutineMaterializationSchema).max(100),
    version: z.number().int().min(1),
    created_at: z.number().int(),
    updated_at: z.number().int(),
    archived_at: z.number().int().nullable(),
  })
  .strict();

export const CreateRoutineInputSchema = z
  .object({
    name: z.string().trim().min(1).max(160),
    child_copy: z.string().trim().min(1).max(160),
    notes: z.string().trim().max(8000).default(""),
    area_id: PlanningOpaqueIdSchema.nullable().default(null),
    project_id: PlanningOpaqueIdSchema.nullable().default(null),
    term_id: PlanningOpaqueIdSchema.nullable().default(null),
    child_ids: z.array(PlanningOpaqueIdSchema).min(1).max(20),
    color: z
      .string()
      .regex(/^#[0-9a-fA-F]{6}$/u)
      .default("#4f8f72"),
    schedule: RoutineScheduleSchema,
    timezone: z.string().min(1).max(64),
    start_local_date: LocalDateSchema,
    end_local_date: LocalDateSchema.nullable().default(null),
    availability: RoutineAvailabilitySchema.default({
      period: "anytime",
      from_local_time: null,
      until_local_time: null,
    }),
    due_offset_days: z.number().int().min(0).max(30).default(0),
    default_duration_minutes: z.number().int().min(0).max(1440).nullable().default(null),
    verification_policy: z.literal("none").default("none"),
    evidence_policy: z
      .object({ required: z.literal("none").default("none") })
      .strict()
      .default({ required: "none" }),
    notification_policy: RoutineNotificationPolicySchema.default({
      child_reminder: false,
      guardian_digest: false,
    }),
    exception_dates: z.array(LocalDateSchema).max(366).default([]),
    pause_ranges: z.array(RoutinePauseRangeSchema).max(52).default([]),
    calendar_ids: z.array(PlanningOpaqueIdSchema).max(20).default([]),
    materialize_from_local_date: LocalDateSchema,
    materialize_until_local_date: LocalDateSchema,
  })
  .strict()
  .refine((value) => new Set(value.child_ids).size === value.child_ids.length, {
    message: "child_ids must be unique",
    path: ["child_ids"],
  })
  .refine((value) => new Set(value.exception_dates).size === value.exception_dates.length, {
    message: "exception_dates must be unique",
    path: ["exception_dates"],
  })
  .refine((value) => new Set(value.calendar_ids).size === value.calendar_ids.length, {
    message: "calendar_ids must be unique",
    path: ["calendar_ids"],
  })
  .refine(
    (value) => value.end_local_date === null || value.end_local_date >= value.start_local_date,
    {
      message: "end_local_date must not be earlier than start_local_date",
      path: ["end_local_date"],
    },
  )
  .refine((value) => value.materialize_until_local_date >= value.materialize_from_local_date, {
    message: "materialization end must not be earlier than start",
    path: ["materialize_until_local_date"],
  })
  .superRefine((value, context) => {
    if (value.notification_policy.child_reminder && value.availability.from_local_time === null) {
      context.addIssue({
        code: "custom",
        message: "child reminders require an explicit availability start time",
        path: ["notification_policy", "child_reminder"],
      });
    }
    if (value.notification_policy.guardian_digest && value.availability.until_local_time === null) {
      context.addIssue({
        code: "custom",
        message: "guardian digests require an explicit availability end time",
        path: ["notification_policy", "guardian_digest"],
      });
    }
  });

export const RoutineCreatePreviewRequestSchema = CreateRoutineInputSchema;

export const RoutinePreviewOccurrenceSchema = z
  .object({ child_id: PlanningOpaqueIdSchema, planned_local_date: LocalDateSchema })
  .strict();

export const RoutineCreatePreviewResponseSchema = z
  .object({
    preview: z
      .object({
        id: PlanningOpaqueIdSchema,
        affected_count: z.number().int().min(0).max(500),
        expires_at: z.iso.datetime(),
        occurrences: z.array(RoutinePreviewOccurrenceSchema).max(500),
      })
      .strict(),
    idempotency_replayed: z.boolean(),
    request_id: PlanningRequestIdSchema,
  })
  .strict();

export const RoutineCreateApplyRequestSchema = z
  .object({
    preview_id: PlanningOpaqueIdSchema,
    expected_affected_count: z.number().int().min(0).max(500),
    confirmation: z.literal("create_routine"),
  })
  .strict();

export const RoutineCreateApplyResponseSchema = z
  .object({
    routine: RoutineSchema,
    materialized_count: z.number().int().min(0).max(500),
    idempotency_replayed: z.boolean(),
    sync_cursor: z.string().min(1),
    request_id: PlanningRequestIdSchema,
  })
  .strict();

export const RoutineListResponseSchema = z
  .object({ items: z.array(RoutineSchema), request_id: PlanningRequestIdSchema })
  .strict();

export const RoutineVersionHistoryResponseSchema = z
  .object({
    routine_id: PlanningOpaqueIdSchema,
    items: z.array(RoutineVersionSchema).max(500),
    next_cursor: z.string().min(1).nullable(),
    request_id: PlanningRequestIdSchema,
  })
  .strict();

export const RoutineVersionComparableFieldSchema = z.enum([
  "schedule",
  "timezone",
  "start_local_date",
  "end_local_date",
  "availability",
  "due_offset_days",
  "default_duration_minutes",
  "verification_policy",
  "evidence_policy",
  "notification_policy",
  "exception_dates",
  "pause_ranges",
  "calendar_ids",
]);

export const RoutineVersionComparisonResponseSchema = z
  .object({
    routine_id: PlanningOpaqueIdSchema,
    from_version: RoutineVersionSchema,
    to_version: RoutineVersionSchema,
    changed_fields: z.array(RoutineVersionComparableFieldSchema),
    request_id: PlanningRequestIdSchema,
  })
  .strict();

export const RoutineReconciliationRunSchema = z
  .object({
    id: PlanningOpaqueIdSchema,
    workspace_id: PlanningOpaqueIdSchema,
    source: z.enum(["web", "mcp", "cron"]),
    from_local_date: LocalDateSchema,
    until_local_date: LocalDateSchema,
    status: z.enum(["queued", "running", "completed", "failed"]),
    missing_count: z.number().int().min(0),
    repaired_count: z.number().int().min(0),
    error_code: z.string().min(1).max(160).nullable(),
    version: z.number().int().min(1),
    created_at: z.number().int(),
    started_at: z.number().int().nullable(),
    completed_at: z.number().int().nullable(),
  })
  .strict();

export const RoutineReconciliationListResponseSchema = z
  .object({
    items: z.array(RoutineReconciliationRunSchema).max(20),
    request_id: PlanningRequestIdSchema,
  })
  .strict();

export const RoutineReconciliationRequestSchema = z
  .object({ window_days: z.number().int().min(7).max(31).default(14) })
  .strict();

export const RoutineReconciliationResponseSchema = z
  .object({
    run: RoutineReconciliationRunSchema,
    idempotency_replayed: z.boolean(),
    request_id: PlanningRequestIdSchema,
  })
  .strict();

export const RoutineStatusCommandRequestSchema = z
  .object({ expected_version: z.number().int().min(1) })
  .strict();

export const RoutineStatusCommandResponseSchema = z
  .object({
    routine: RoutineSchema,
    command: z.enum(["pause", "resume"]),
    idempotency_replayed: z.boolean(),
    sync_cursor: z.string().min(1),
    request_id: PlanningRequestIdSchema,
  })
  .strict();

export const UpdateRoutineInputSchema = z
  .object({
    expected_version: z.number().int().min(1),
    name: z.string().trim().min(1).max(160),
    child_copy: z.string().trim().min(1).max(160),
    notes: z.string().trim().max(8000).default(""),
    area_id: PlanningOpaqueIdSchema.nullable().default(null),
    project_id: PlanningOpaqueIdSchema.nullable().default(null),
    term_id: PlanningOpaqueIdSchema.nullable().default(null),
    child_ids: z.array(PlanningOpaqueIdSchema).min(1).max(20),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/u),
    schedule: RoutineScheduleSchema,
    timezone: z.string().min(1).max(64),
    start_local_date: LocalDateSchema,
    end_local_date: LocalDateSchema.nullable().default(null),
    availability: RoutineAvailabilitySchema,
    due_offset_days: z.number().int().min(0).max(30),
    default_duration_minutes: z.number().int().min(0).max(1440).nullable(),
    verification_policy: z.literal("none"),
    evidence_policy: z.object({ required: z.literal("none") }).strict(),
    notification_policy: RoutineNotificationPolicySchema,
    exception_dates: z.array(LocalDateSchema).max(366),
    pause_ranges: z.array(RoutinePauseRangeSchema).max(52),
    calendar_ids: z.array(PlanningOpaqueIdSchema).max(20),
    effective_from_local_date: LocalDateSchema,
    materialize_until_local_date: LocalDateSchema,
  })
  .strict()
  .refine((value) => new Set(value.child_ids).size === value.child_ids.length, {
    message: "child_ids must be unique",
    path: ["child_ids"],
  })
  .refine((value) => new Set(value.exception_dates).size === value.exception_dates.length, {
    message: "exception_dates must be unique",
    path: ["exception_dates"],
  })
  .refine((value) => new Set(value.calendar_ids).size === value.calendar_ids.length, {
    message: "calendar_ids must be unique",
    path: ["calendar_ids"],
  })
  .refine(
    (value) => value.end_local_date === null || value.end_local_date >= value.start_local_date,
    {
      message: "end_local_date must not be earlier than start_local_date",
      path: ["end_local_date"],
    },
  )
  .refine((value) => value.materialize_until_local_date >= value.effective_from_local_date, {
    message: "materialization end must not be earlier than effective date",
    path: ["materialize_until_local_date"],
  })
  .superRefine((value, context) => {
    if (value.notification_policy.child_reminder && value.availability.from_local_time === null) {
      context.addIssue({
        code: "custom",
        message: "child reminders require an explicit availability start time",
        path: ["notification_policy", "child_reminder"],
      });
    }
    if (value.notification_policy.guardian_digest && value.availability.until_local_time === null) {
      context.addIssue({
        code: "custom",
        message: "guardian digests require an explicit availability end time",
        path: ["notification_policy", "guardian_digest"],
      });
    }
  });

export const RoutineUpdatePreviewRequestSchema = UpdateRoutineInputSchema;

export const RoutineUpdatePreviewItemSchema = z
  .object({
    child_id: PlanningOpaqueIdSchema,
    planned_local_date: LocalDateSchema,
    task_id: PlanningOpaqueIdSchema.nullable(),
    action: z.enum(["create", "update", "cancel", "protected"]),
    protected_reason: z
      .enum(["started_or_finished", "individually_changed", "already_cancelled"])
      .nullable(),
  })
  .strict();

export const RoutineUpdatePreviewResponseSchema = z
  .object({
    preview: z
      .object({
        id: PlanningOpaqueIdSchema,
        routine_id: PlanningOpaqueIdSchema,
        next_version_number: z.number().int().min(2),
        affected_count: z.number().int().min(0).max(500),
        protected_count: z.number().int().min(0).max(500),
        expires_at: z.iso.datetime(),
        items: z.array(RoutineUpdatePreviewItemSchema).max(1000),
      })
      .strict(),
    idempotency_replayed: z.boolean(),
    request_id: PlanningRequestIdSchema,
  })
  .strict();

export const RoutineUpdateApplyRequestSchema = z
  .object({
    preview_id: PlanningOpaqueIdSchema,
    expected_affected_count: z.number().int().min(0).max(500),
    confirmation: z.literal("update_routine"),
  })
  .strict();

export const RoutineUpdateApplyResponseSchema = z
  .object({
    routine: RoutineSchema,
    created_count: z.number().int().min(0).max(500),
    updated_count: z.number().int().min(0).max(500),
    cancelled_count: z.number().int().min(0).max(500),
    protected_count: z.number().int().min(0).max(500),
    idempotency_replayed: z.boolean(),
    sync_cursor: z.string().min(1),
    request_id: PlanningRequestIdSchema,
  })
  .strict();
